import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import supabase from "../services/supabaseClient";
import { uploadRoomFile, deleteRoomFile } from "../lib/roomFileStorage";
import { listBookshelfDocuments, upsertBookshelfDocument, deleteBookshelfDocument } from "../lib/bookshelf";

// ═══════════════════════════════════════════════════════════════
// BOOKSHELF CONTEXT — Shared document store for all hotspots
// Any panel can save to, read from, and recall documents
// Persists to the bookshelf_documents Supabase table (see lib/bookshelf),
// scoped to the signed-in user, so the list follows the user across devices.
// Real uploaded files are stored in Supabase storage (see lib/roomFileStorage)
// and referenced here by storagePath.
// ═══════════════════════════════════════════════════════════════

// One-time migration: documents saved by this app before it moved off
// localStorage. Adopted into Supabase the first time a signed-in user with an
// empty Bookshelf loads, then cleared so it can't run again or resurrect
// deleted docs later.
const LEGACY_LOCAL_STORAGE_KEY = "neuroquest_bookshelf";

// --- Document shape ---
// {
//   id: string,
//   title: string,
//   type: "document" | "whiteboard-cards" | "whiteboard-canvas" | "calendar-export" | "note" | "reference" | "upload",
//   content: string | object,       // text content, JSON, or base64 for files
//   sourceHotspot: "desk" | "whiteboard" | "calendar" | "couch" | "upload",
//   mimeType: string | null,        // for uploaded files
//   fileName: string | null,        // original filename for uploads
//   fileSize: number | null,        // bytes
//   storagePath: string | null,     // Supabase storage path, for real uploads
//   roomId: string | null,          // room this doc belongs to; null = visible everywhere (seed docs)
//   status: "draft" | "final" | "archived",
//   tags: string[],
//   createdAt: number,              // timestamp
//   updatedAt: number,              // timestamp
//   lastOpenedBy: string | null,    // which hotspot last opened this
// }

function generateId() {
  return `doc-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
}

function getIcon(type, mimeType) {
  if (mimeType) {
    if (mimeType.startsWith("image/")) return "🖼";
    if (mimeType.includes("pdf")) return "📄";
    if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) return "📊";
    if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "📽";
    if (mimeType.includes("word") || mimeType.includes("document")) return "📝";
  }
  switch (type) {
    case "document": return "📝";
    case "whiteboard-cards": return "🖼";
    case "whiteboard-canvas": return "🎨";
    case "calendar-export": return "📅";
    case "note": return "📋";
    case "reference": return "🔗";
    case "upload": return "📎";
    default: return "📄";
  }
}

function getStatusColor(status) {
  switch (status) {
    case "draft": return { color: "#EAB308", bg: "rgba(234,179,8,0.12)", border: "rgba(234,179,8,0.3)" };
    case "final": return { color: "#22C55E", bg: "rgba(34,197,94,0.12)", border: "rgba(34,197,94,0.3)" };
    case "archived": return { color: "#64748B", bg: "rgba(100,116,139,0.12)", border: "rgba(100,116,139,0.3)" };
    default: return { color: "#94A3B8", bg: "rgba(148,163,184,0.12)", border: "rgba(148,163,184,0.3)" };
  }
}

async function migrateLegacyLocalDocuments(userId) {
  try {
    const raw = localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY);
    if (!raw) return [];
    const legacyDocs = JSON.parse(raw);
    if (!Array.isArray(legacyDocs) || legacyDocs.length === 0) return [];
    for (const doc of legacyDocs) {
      await upsertBookshelfDocument(userId, doc);
    }
    localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY);
    return legacyDocs;
  } catch (e) {
    console.error("Failed to migrate legacy local bookshelf documents:", e);
    return [];
  }
}

const BookshelfContext = createContext(null);

export function BookshelfProvider({ children }) {
  const [documents, setDocuments] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [storageError, setStorageError] = useState(false);
  const [userId, setUserId] = useState(null);

  // Always-current snapshot of `documents`, read inside callbacks so their
  // identity can stay stable without listing `documents` as a dependency.
  const documentsRef = useRef(documents);
  useEffect(() => { documentsRef.current = documents; }, [documents]);

  // Track the signed-in user directly (this provider sits above SpatialProvider,
  // where the resolved `user` normally lives, so it can't read it from there).
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setUserId(data?.session?.user?.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => { mounted = false; sub?.subscription?.unsubscribe(); };
  }, []);

  // Hydrate from Supabase whenever the signed-in user changes.
  useEffect(() => {
    if (!userId) {
      setDocuments([]);
      setLoaded(false);
      return;
    }
    let cancelled = false;
    setLoaded(false);
    (async () => {
      try {
        const docs = await listBookshelfDocuments(userId);
        if (cancelled) return;
        if (docs.length === 0) {
          const migrated = await migrateLegacyLocalDocuments(userId);
          if (!cancelled) setDocuments(migrated);
        } else {
          setDocuments(docs);
        }
        if (!cancelled) setLoaded(true);
      } catch (e) {
        console.error("Failed to load bookshelf documents:", e);
        if (!cancelled) {
          setDocuments([]);
          setLoaded(true);
          setStorageError(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const persist = useCallback((doc) => {
    if (!userId) return;
    upsertBookshelfDocument(userId, doc)
      .then(() => setStorageError(false))
      .catch((e) => {
        console.error("Failed to save bookshelf document:", e);
        setStorageError(true);
      });
  }, [userId]);

  const mergeDocument = useCallback((doc) => {
    const now = Date.now();
    const base = {
      id: doc.id || generateId(),
      title: doc.title || "Untitled",
      type: doc.type || "document",
      priorType: doc.priorType || null,
      slot: Number.isInteger(doc.slot) ? doc.slot : null,
      content: doc.content || "",
      sourceHotspot: doc.sourceHotspot || "desk",
      mimeType: doc.mimeType || null,
      fileName: doc.fileName || null,
      fileSize: doc.fileSize || null,
      storagePath: doc.storagePath || null,
      roomId: doc.roomId || null,
      status: doc.status || "draft",
      tags: doc.tags || [],
      createdAt: doc.createdAt || now,
      updatedAt: now,
      lastOpenedBy: null,
    };

    const existing = documentsRef.current.find(d => d.id === base.id);
    return existing ? {
      ...existing,
      ...base,
      createdAt: existing.createdAt || base.createdAt,
      status: doc.status || existing.status,
      tags: doc.tags || existing.tags,
      lastOpenedBy: existing.lastOpenedBy || null,
      updatedAt: now,
    } : base;
  }, []);

  const applyDocument = useCallback((merged) => {
    setDocuments(prev => {
      const idx = prev.findIndex(d => d.id === merged.id);
      const next = idx >= 0
        ? prev.map((d, i) => (i === idx ? merged : d))
        : [merged, ...prev];
      documentsRef.current = next;
      return next;
    });
  }, []);

  const saveToBookshelf = useCallback((doc) => {
    const merged = mergeDocument(doc);
    applyDocument(merged);
    persist(merged);
    return merged.id;
  }, [mergeDocument, applyDocument, persist]);

  const saveToBookshelfAsync = useCallback(async (doc) => {
    if (!userId) throw new Error("Not signed in — can't save to the Bookshelf.");
    const merged = mergeDocument(doc);
    try {
      await upsertBookshelfDocument(userId, merged);
      setStorageError(false);
    } catch (e) {
      console.error("Failed to save bookshelf document:", e);
      setStorageError(true);
      throw e;
    }
    applyDocument(merged);
    return merged.id;
  }, [userId, mergeDocument, applyDocument]);

  // --- GET: Retrieve a document by ID ---
  const getDocument = useCallback((id) => {
    return documents.find(d => d.id === id) || null;
  }, [documents]);

  // --- LIST: Browse with optional filters ---
  const listDocuments = useCallback((filters = {}) => {
    let result = [...documents];

    if (filters.roomId) {
      result = result.filter(d => !d.roomId || d.roomId === filters.roomId);
    }
    if (filters.type) {
      result = result.filter(d => d.type === filters.type);
    }
    if (filters.sourceHotspot) {
      result = result.filter(d => d.sourceHotspot === filters.sourceHotspot);
    }
    if (filters.status) {
      result = result.filter(d => d.status === filters.status);
    }
    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(d =>
        d.title.toLowerCase().includes(q) ||
        (typeof d.content === "string" && d.content.toLowerCase().includes(q)) ||
        d.tags.some(t => t.toLowerCase().includes(q))
      );
    }
    if (filters.tags && filters.tags.length > 0) {
      result = result.filter(d => filters.tags.some(t => d.tags.includes(t)));
    }

    // Sort by most recently updated
    result.sort((a, b) => b.updatedAt - a.updatedAt);

    return result;
  }, [documents]);

  // --- UPDATE: Modify an existing document ---
  const updateDocument = useCallback((id, changes) => {
    const existing = documentsRef.current.find(d => d.id === id);
    if (!existing) return;
    const merged = { ...existing, ...changes, updatedAt: Date.now() };
    setDocuments(prev => prev.map(d => d.id === id ? merged : d));
    persist(merged);
  }, [persist]);

  // --- DELETE: Remove a document (and its file in storage, if any) ---
  const deleteDocument = useCallback((id) => {
    const doc = documentsRef.current.find(d => d.id === id);
    if (doc?.storagePath) {
      deleteRoomFile(doc.storagePath).catch((e) => {
        console.error("Failed to delete room file from storage:", e);
      });
    }
    setDocuments(prev => {
      const next = prev.filter(d => d.id !== id);
      documentsRef.current = next;
      return next;
    });
    if (userId) {
      deleteBookshelfDocument(userId, id).catch((e) => {
        console.error("Failed to delete bookshelf document:", e);
        setStorageError(true);
      });
    }
  }, [userId]);

  // --- MARK OPENED: Track which hotspot last opened this ---
  const markOpened = useCallback((id, hotspot) => {
    const existing = documentsRef.current.find(d => d.id === id);
    if (!existing) return;
    const merged = { ...existing, lastOpenedBy: hotspot, updatedAt: Date.now() };
    setDocuments(prev => prev.map(d => d.id === id ? merged : d));
    persist(merged);
  }, [persist]);

  const uniqueTitleInRoom = useCallback((base, roomId) => {
    const taken = new Set(
      documentsRef.current
        .filter((d) => !d.roomId || d.roomId === roomId)
        .map((d) => d.title)
    );
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base} (${n})`)) n += 1;
    return `${base} (${n})`;
  }, []);

  // --- UPLOAD FILE: Upload to this room's Supabase storage, then register it ---
  const uploadFileToRoom = useCallback(async (file, { userId: uploaderId, roomId } = {}) => {
    if (!file) throw new Error("uploadFileToRoom requires a file");
    if (!uploaderId || !roomId) throw new Error("uploadFileToRoom requires userId and roomId");

    const { storagePath, fileName, mimeType, fileSize } = await uploadRoomFile(uploaderId, roomId, file);

    const baseTitle = (fileName || file.name || "file").replace(/\.[^/.]+$/, "");

    return saveToBookshelfAsync({
      title: uniqueTitleInRoom(baseTitle, roomId),
      type: "upload",
      content: "",
      sourceHotspot: "upload",
      mimeType,
      fileName,
      fileSize,
      storagePath,
      roomId,
      status: "final",
    });
  }, [saveToBookshelfAsync, uniqueTitleInRoom]);

  const value = {
    documents,
    loaded,
    storageError,
    saveToBookshelf,
    saveToBookshelfAsync,
    getDocument,
    listDocuments,
    updateDocument,
    deleteDocument,
    markOpened,
    uploadFileToRoom,
    getIcon,
    getStatusColor,
  };

  return (
    <BookshelfContext.Provider value={value}>
      {children}
    </BookshelfContext.Provider>
  );
}

export function useBookshelf() {
  const ctx = useContext(BookshelfContext);
  if (!ctx) throw new Error("useBookshelf must be used inside BookshelfProvider");
  return ctx;
}

export default BookshelfContext;
